import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  B2,
  B3,
  B4,
  B5,
  Box,
  Button,
  ButtonGroup,
  Combobox,
  P,
  Spinner,
  Stack,
  Text
} from "@adfenix/alexandria";

import UilSwitchIcon from "@iconscout/react-unicons/icons/uil-exchange";
import UilLock from "@iconscout/react-unicons/icons/uil-lock";

import { Modal } from "components/Modal";
import Page from "components/Page";
import Card from "components/Card";
import PageHeader from "components/PageHeader";
import Divider from "components/Divider";
import { OfficeStripeCardHandler } from "components/StripeComponent/Office/OfficeStripeCardHandler";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getOfficeInvoiceInfo } from "api/services/billingService";
import { getUserOffices } from "api/services/configService";
import { getOfficeStripePaymentMethods } from "api/services/stripeService";
import { StripeTransactionHistory } from "./TransactionHistory/StripeTransactionHistory";
import { InvoiceHistory } from "./InvoiceHistory";
import { ConfigurationService } from "@adfenix/types";

export const OfficeBillingPage = () => {
  const navigate = useNavigate();

  const [showAddCreditCardComponent, setShowAddCreditCardComponent] =
    useState(false);

  const [showSwtichOfficeModal, setShowSwtichOfficeModal] = useState(false);

  const [selectedOffice, setSelectedOffice] =
    useState<ConfigurationService.OfficeForTokenModel>();

  const [selectedOfficeId, setSelectedOfficeId] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();

  const {
    isInitialLoading: loadingCardInfo,
    data: creditCardsInfo,
    refetch: updateCardInfo
  } = useQuery(
    ["getOfficeStripePaymentMethods", selectedOfficeId],
    () => getOfficeStripePaymentMethods(selectedOfficeId!),
    {
      enabled: Boolean(selectedOfficeId),
      retry: false
    }
  );

  const { data: invoiceInfo, refetch: refetchInvoiceInfo } = useQuery(
    ["getOfficeInvoiceInfo", selectedOfficeId],
    () => getOfficeInvoiceInfo(selectedOfficeId!),
    {
      enabled: false,
      retry: false
    }
  );

  const { data: userOffices, isLoading: isLoadingUserOffices } = useQuery(
    ["getUserOffices"],
    () => getUserOffices()
  );

  useEffect(() => {
    if (creditCardsInfo !== undefined && creditCardsInfo?.length) {
      setShowAddCreditCardComponent(true);
    }
  }, [creditCardsInfo]);

  useEffect(() => {
    if (!selectedOfficeId && userOffices && userOffices.length === 1) {
      setShowSwtichOfficeModal(false);
      setSelectedOffice(userOffices[0]);
      return setSelectedOfficeId(userOffices[0].officeId);
    }

    if (!selectedOfficeId && userOffices && userOffices.length > 1) {
      return setShowSwtichOfficeModal(true);
    }
  }, [selectedOfficeId, userOffices]);

  useEffect(() => {
    if (selectedOfficeId) {
      refetchInvoiceInfo();
    }
  }, [selectedOfficeId, refetchInvoiceInfo]);

  const defaultItem = userOffices?.find(
    office => office.officeId === selectedOfficeId
  );

  if ((isLoadingUserOffices && userOffices !== undefined) || loadingCardInfo)
    return <Spinner centered />;

  const onClose = () => {
    if (selectedOfficeId) {
      setShowSwtichOfficeModal(false);
      return;
    }

    navigate("/property/list");
  };

  const handleOfficeSelection = () => {
    setSelectedOfficeId(selectedOffice?.officeId);
    setShowSwtichOfficeModal(false);
  };

  const isCardListEmpty =
    creditCardsInfo === undefined || !creditCardsInfo?.length;

  return (
    <Page
      header={
        <Box>
          <PageHeader title={t("billing_office_page_title")} />

          {selectedOfficeId ? (
            <>
              {userOffices?.length === 0 ? (
                <Text>
                  {t("billing_page_sub_title")}{" "}
                  <B5 color="grey">{selectedOffice?.officeName}</B5>
                </Text>
              ) : (
                <Button
                  logLabel="billing-switch-office-button"
                  variant="text"
                  onClick={() => setShowSwtichOfficeModal(true)}
                >
                  <Box display="flex" alignItems="self-end">
                    <Text>
                      {t("billing_page_sub_title")}{" "}
                      <B5 color="grey">{selectedOffice?.officeName} </B5>
                    </Text>
                    &nbsp;
                    <UilSwitchIcon />
                  </Box>
                </Button>
              )}
            </>
          ) : null}
        </Box>
      }
    >
      <Stack spacing="s">
        <B2>{t("billing_page_payment_preferences_title")}</B2>
        <P>{t("billing_page_office_payment_preferences_sub_title")}</P>
      </Stack>

      <Stack spacing="gutter" pY="xl2">
        <Card>
          <B3>{t("billing_invoice_card_title")}</B3>

          <Box pY="xs">
            <P>
              {t("billing_invoice_card_email")}{" "}
              {invoiceInfo?.email ?? t("billing_invoice_card_email_not_found")}
            </P>
          </Box>
        </Card>

        <Card>
          <Stack spacing="m">
            <B3>{t("billing_credit_card_card_title")}</B3>
            <P>
              {isCardListEmpty
                ? t("billing_empty_credit_card_office_sub_title")
                : t("billing_credit_card_office_sub_title")}
            </P>

            {selectedOfficeId && showAddCreditCardComponent && (
              <>
                <Stack spacing="xs">
                  <OfficeStripeCardHandler
                    loadingCardInfo={loadingCardInfo}
                    updateCardInfo={updateCardInfo}
                    creditCardsInfo={creditCardsInfo}
                    isCardListEmpty={isCardListEmpty}
                    handleCancel={() => setShowAddCreditCardComponent(false)}
                    officeId={selectedOfficeId!}
                  />
                </Stack>
              </>
            )}

            {selectedOfficeId &&
            isCardListEmpty &&
            !showAddCreditCardComponent ? (
              <Box pY="l">
                <Button
                  logLabel="office-add-card-button"
                  onClick={() => setShowAddCreditCardComponent(true)}
                >
                  {t("billing_add_card_card_button_title")}
                </Button>
              </Box>
            ) : (
              <Box display="flex" alignItems="center">
                <UilLock size="16px" />
                <Text>{t("billing_stripe_encrypted_transactions")}</Text>&nbsp;
                <a
                  href="https://stripe.com"
                  data-loglabel="stripe-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  Stripe
                </a>
              </Box>
            )}
          </Stack>
        </Card>
      </Stack>

      <Divider margin="24px 0" />

      <B2>{t("billing_history_title")}</B2>

      <Stack spacing="xl4" pY="s">
        <>
          <B4>{t("billing_invoice_history_card_title")}</B4>

          <Box pY="xs2">
            <InvoiceHistory officeId={selectedOfficeId} mode="office" />
          </Box>
        </>

        <>
          <B4>{t("billing_credit_card_history_card_title")}</B4>

          <Box pY="xs2">
            {selectedOfficeId && (
              <StripeTransactionHistory
                officeId={selectedOfficeId}
                mode="office"
              />
            )}
          </Box>
        </>
      </Stack>

      <Modal show={showSwtichOfficeModal} onClose={onClose}>
        <Modal.Card>
          <Modal.Card.Head onClose={onClose}>
            <Modal.Card.Title>
              {t("billing_office_select_office")}
            </Modal.Card.Title>
          </Modal.Card.Head>

          <Modal.Card.Body>
            <Box pX="xs" style={{ minHeight: "324px" }}>
              {userOffices && (
                <Combobox
                  items={userOffices!}
                  defaultItem={defaultItem}
                  valueObjectIndexAccessor="officeName"
                  onChange={office => setSelectedOffice(office)}
                  placeholder={t("analytics.select.office.title")}
                  disabled={!userOffices}
                  classNamePrefix="billing-office-combobox"
                />
              )}
            </Box>
          </Modal.Card.Body>
          <Modal.Card.Foot style={{ alignItems: "normal" }}>
            <ButtonGroup>
              <Button
                logLabel="modal-select-office-close-button"
                variant="secondary"
                onClick={onClose}
              >
                {t("billing_office_select_office_modal_close_button")}
              </Button>

              <Button
                logLabel="modal-select-office-button"
                type="submit"
                variant="primary"
                onClick={handleOfficeSelection}
              >
                {t("billing_office_select_office_modal_button")}
              </Button>
            </ButtonGroup>
          </Modal.Card.Foot>
        </Modal.Card>
      </Modal>
    </Page>
  );
};
